import {
  getFormattedPrice,
  isImageOr3dModel,
  MediaCardItem,
  MediaListItem,
  processAttachment,
} from "@msys/ui";
import { useTolgee, useTranslate } from "@tolgee/react";
import React from "react";
import { Link, To } from "react-router-dom";
import { PimSearchGroupedProductsNodeFragment } from "../../../main-routes/products/ProductsCatalogues.generated.js";
import type { ProductListItems_ProductFragment } from "../../productLists/ProductListItems.generated.js";
import { getProductBrand, getProductFullDescription } from "../helper.js";
import { ProductSearchItem__ProductSearchResultFragment } from "../Product.generated.js";
import { SupplierProductStatusChip } from "../../../main-routes/products/SupplierProductStatusChip.js";
import { SxProps, Theme, Typography } from "@mui/material";

/**
 * The product shapes `ProductsListView` / `ProductItem` can render: PIM search
 * results, grouped catalogue products, and resolved PIM products (product-list
 * items). They differ only in `brand` (scalar) vs `branding` — see `getProductBrand`.
 */
export type ProductsListViewProduct =
  | ProductSearchItem__ProductSearchResultFragment
  | PimSearchGroupedProductsNodeFragment["product"]
  | ProductListItems_ProductFragment;

interface Props<P extends ProductsListViewProduct> {
  product: P;
  ActionButton: React.ComponentType<{ product: P }>;
  onClick?: (product: P) => void;
  getProductTo?: (product: P) => To;
  sx?: SxProps<Theme>;
  /** Marks this product as the one currently assigned to the caller's entity. */
  selected?: boolean;
}

interface ListItemProps<P extends ProductsListViewProduct> extends Props<P> {
  /** Rendered ahead of the product's availability status. */
  StatusIcons?: React.ReactNode[];
}

export const ProductCardItem = <P extends ProductsListViewProduct>({
  product,
  ActionButton,
  onClick,
  getProductTo,
  sx,
  selected,
}: Props<P>) => {
  // const attachments = product.attachments.filter(attachment => {
  //   if (attachment.group === "3dItemDefinition") {
  //     return (
  //       attachment.title === "thumbnail.jpg" ||
  //       attachment.title === "thumbnail.png"
  //     );
  //   }
  //   return true;
  // });
  const pictures = product.attachments
    .map(attachment =>
      processAttachment({
        ...attachment,
        id: attachment.id.toString(),
      })
    )
    .filter(isImageOr3dModel);
  const fullDescription = getProductFullDescription(product).concat(
    "\n\n",
    `${product.articleNumber} ${product.supplier.title}`
  );
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Product"]);

  const manufacturerLabel = product.manufacturerArticleNumber
    ? `${t("Manufacturer article no.", { ns: "Product" })}: ${product.manufacturerArticleNumber}`
    : null;

  const info = manufacturerLabel ? (
    <Typography variant="body2" sx={{ color: "grey.600" }}>
      {manufacturerLabel}
    </Typography>
  ) : undefined;

  return (
    <MediaCardItem
      key={product.id}
      selected={selected}
      // `selected` is styling only; this conveys the state to assistive tech.
      // Not `aria-selected` — the root is a Card with no role.
      aria-current={selected ? "true" : undefined}
      // Rendered as a Link when getProductTo is set; disable native anchor drag
      // so interacting with inner controls (e.g. a quantity input) doesn't start
      // dragging the card.
      draggable={false}
      // @ts-ignore
      onClick={
        onClick
          ? e => {
              e.stopPropagation();
              onClick(product);
            }
          : undefined
      }
      {...(getProductTo
        ? {
            component: Link,
            to: getProductTo(product),
          }
        : undefined)}
      title={product.texts?.title ?? ""}
      article={
        product.articleNumber
          ? `${t("Article no.", { ns: "Product" })}: ${product.articleNumber}`
          : undefined
      }
      brand={getProductBrand(product) ?? undefined}
      description={fullDescription}
      descriptionMaxNumberOfLines={1}
      price={`${
        product.pricing?.netPrice
          ? getFormattedPrice(product.pricing?.netPrice, language)
          : "–"
      } / ${
        product.pricing?.listPrice
          ? getFormattedPrice(product.pricing?.listPrice, language)
          : "–"
      }`}
      imageUrl={pictures[0]?.url}
      images={pictures}
      ActionButton={<ActionButton product={product} />}
      StatusIcons={
        product.status && product.status.value !== "available"
          ? [<SupplierProductStatusChip status={product.status.value} />]
          : undefined
      }
      sx={sx}
      Info={info}
    />
  );
};

export const ProductListItem = <P extends ProductsListViewProduct>({
  product,
  ActionButton,
  onClick,
  getProductTo,
  sx,
  selected,
  StatusIcons = [],
}: ListItemProps<P>) => {
  const language = useTolgee(["language"]).getLanguage()!;
  const { t } = useTranslate(["Product"]);
  // const attachments = product.attachments.filter(attachment => {
  //   if (attachment.group === "3dItemDefinition") {
  //     return (
  //       attachment.title === "thumbnail.jpg" ||
  //       attachment.title === "thumbnail.png"
  //     );
  //   }
  //   return true;
  // });
  const pictures = product.attachments
    .map(attachment =>
      processAttachment({
        ...attachment,
        id: attachment.id.toString(),
      })
    )
    .filter(isImageOr3dModel);
  const fullDescription = getProductFullDescription(product).concat(
    "\n\n",
    `${product.articleNumber} ${product.supplier.title}`
  );

  const articleLabel = product.articleNumber
    ? `${t("Article no.", { ns: "Product" })}: ${product.articleNumber}`
    : null;

  const manufacturerLabel = product.manufacturerArticleNumber
    ? `${t("Manufacturer article no.", { ns: "Product" })}: ${product.manufacturerArticleNumber}`
    : null;

  const listArticle = [articleLabel, manufacturerLabel]
    .filter(Boolean)
    .join(" • ");

  return (
    <MediaListItem
      key={product.id}
      selected={selected}
      // `selected` is styling only; this conveys the state to assistive tech.
      // Not `aria-selected` — the root is a Card with no role.
      aria-current={selected ? "true" : undefined}
      // Rendered as a Link when getProductTo is set; disable native anchor drag
      // so interacting with inner controls (e.g. a quantity input) doesn't start
      // dragging the card.
      draggable={false}
      // @ts-ignore
      onClick={
        onClick
          ? e => {
              e.stopPropagation();
              onClick(product);
            }
          : undefined
      }
      {...(getProductTo
        ? {
            component: Link,
            to: getProductTo(product),
          }
        : undefined)}
      data-testid="product-search-result"
      data-article-number={product.articleNumber}
      title={product.texts?.title ?? ""}
      article={listArticle || undefined}
      brand={getProductBrand(product) ?? undefined}
      description={fullDescription}
      descriptionMaxNumberOfLines={1}
      price={`${
        product.pricing?.netPrice
          ? getFormattedPrice(product.pricing?.netPrice, language)
          : "–"
      } / ${
        product.pricing?.listPrice
          ? getFormattedPrice(product.pricing?.listPrice, language)
          : "–"
      }`}
      imageUrl={pictures[0]?.url}
      images={pictures}
      ActionButton={<ActionButton product={product} />}
      StatusIcons={[
        ...StatusIcons,
        ...(product.status && product.status.value !== "available"
          ? [
              <SupplierProductStatusChip
                key="availability"
                status={product.status.value}
              />,
            ]
          : []),
      ]}
      sx={sx}
    />
  );
};
